import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Users, MessageSquare, ShieldCheck, Inbox, Sparkles, MapPin } from "lucide-react";
import { SpotsMeter } from "@/components/site/SpotsMeter";
import { STATES, stateSlug } from "@/lib/constants";

const POPULAR = ["CA", "TX", "FL", "NY", "WA", "AZ", "CO", "GA", "NC", "IL", "NV", "TN"];

const BrowseStates = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="home-browse-states">
    <span className="eyebrow">Browse by state</span>
    <h2 className="font-display text-3xl sm:text-4xl font-black mt-4 tracking-tight">Find a QI where your property is.</h2>
    <div className="flex flex-wrap gap-2 mt-8">
      {POPULAR.map((c) => (
        <Link key={c} to={`/1031-exchange/${stateSlug(c)}`} data-testid={`home-state-${c.toLowerCase()}`}
          className="text-sm bg-white/[0.04] border border-white/10 rounded-full px-4 py-2 text-white/70 hover:border-qi-pink/50 hover:text-white transition-colors">
          {STATES.find((s) => s[0] === c)[1]}
        </Link>
      ))}
      <Link to="/1031-exchange" className="text-sm rounded-full px-4 py-2 text-qi-pink font-bold" data-testid="home-all-states-link">All 50 states →</Link>
    </div>
  </section>
);

const STEPS = [
  { icon: Sparkles, t: "Register & complete intake", d: "Tell us about your firm, coverage, credentials and growth goals in about five minutes." },
  { icon: ShieldCheck, t: "We review your listing", d: "Every profile is reviewed before it goes live so investors only see real, qualified intermediaries." },
  { icon: Inbox, t: "Investors reach you directly", d: "Inquiries land in your private inbox with property value and exchange timeline attached." },
  { icon: MessageSquare, t: "Network with other QIs", d: "Message fellow intermediaries for referrals, overflow work and out-of-state help." },
];

const Hero = () => {
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const navigate = useNavigate();
  const go = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (state) p.set("state", state);
    navigate(`/directory?${p.toString()}`);
  };
  return (
    <section className="relative overflow-hidden grain">
      <img src="/hero.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/90 via-ink/75 to-ink" />
      <div className="absolute -top-40 -right-40 w-[38rem] h-[38rem] bg-qi-pink/20 rounded-full blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32">
        <span className="eyebrow rise">The 1031 intermediary directory</span>
        <h1 data-testid="home-hero-title" className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight leading-[1.02] mt-5 max-w-4xl rise d1">
          Find a qualified intermediary <span className="text-qi-pink">you can actually trust.</span>
        </h1>
        <p className="mt-6 text-base md:text-lg text-white/65 max-w-2xl leading-relaxed rise d2">
          Reviewed QI listings, real credentials up front, and a direct line to the firm — for real estate investors starting a 1031 exchange.
        </p>
        <form onSubmit={go} className="mt-10 flex flex-col md:flex-row gap-3 max-w-3xl bg-white/[0.06] border border-white/10 backdrop-blur-xl rounded-[28px] md:rounded-full p-2 rise d3">
          <div className="flex items-center gap-3 flex-1 px-4">
            <Search size={18} className="text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Firm name or city" data-testid="hero-search-query-input"
              className="bg-transparent w-full py-3 text-sm text-white placeholder:text-white/40 focus:outline-none" />
          </div>
          <div className="flex items-center gap-2 md:border-l border-white/10 px-4">
            <MapPin size={16} className="text-white/40" />
            <select value={state} onChange={(e) => setState(e.target.value)} data-testid="hero-search-state-input"
              className="bg-transparent py-3 text-sm text-white focus:outline-none w-full md:w-44">
              <option value="" className="bg-ink">Any state</option>
              {STATES.map(([c, n]) => <option key={c} value={c} className="bg-ink">{n}</option>)}
            </select>
          </div>
          <button className="btn-pink" data-testid="hero-search-button">Search QIs <ArrowRight size={16} /></button>
        </form>
        <div className="mt-8 rise d4"><SpotsMeter /></div>
      </div>
    </section>
  );
};

const HowItWorks = () => (
  <section id="how" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 scroll-mt-20">
    <div className="grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <span className="eyebrow">How it works</span>
        <h2 className="font-display text-3xl sm:text-4xl font-black mt-4 tracking-tight">Built for QIs. Trusted by investors.</h2>
        <p className="text-white/55 mt-4 leading-relaxed">Legacy directories are a static list of names. We give intermediaries a real profile, a lead inbox and a private peer network.</p>
      </div>
      <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
        {STEPS.map((s, i) => (
          <div key={s.t} className="glass-card p-7 hover:border-qi-pink/40 transition-colors">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-2xl bg-qi-pink/15 text-qi-pink flex items-center justify-center"><s.icon size={22} /></div>
              <span className="font-mono text-xs text-white/30">0{i + 1}</span>
            </div>
            <h3 className="font-display text-lg font-bold mt-5">{s.t}</h3>
            <p className="text-sm text-white/55 mt-2 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FoundingCTA = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10">
    <div className="relative overflow-hidden rounded-[40px] bg-white text-ink p-10 md:p-16 grid md:grid-cols-12 gap-10 items-center">
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-qi-pink/20 rounded-full blur-3xl" />
      <div className="md:col-span-8 relative">
        <span className="font-mono text-xs uppercase tracking-[0.25em] font-bold text-qi-pink">Founding member offer</span>
        <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight mt-4">The first 30 QIs list free.</h2>
        <p className="mt-4 text-slate-600 max-w-xl">Claim a founding spot to get a reviewed listing, an investor lead inbox and access to the QI-to-QI chat network.</p>
      </div>
      <div className="md:col-span-4 relative flex md:justify-end">
        <Link to="/login?mode=register" className="bg-qi-pink text-white rounded-full px-8 py-4 font-bold inline-flex items-center gap-2 hover:-translate-y-1 transition-transform shadow-xl shadow-qi-pink/30" data-testid="cta-claim-spot-button">
          Claim my free spot <ArrowRight size={18} />
        </Link>
      </div>
    </div>
  </section>
);

const InvestorStrip = () => (
  <section className="border-y border-white/10 bg-white/[0.02]">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-qi-emerald/15 text-qi-emerald flex items-center justify-center"><Users size={22} /></div>
        <div>
          <p className="font-display text-lg font-bold">Selling investment property?</p>
          <p className="text-sm text-white/50">Your QI must be in place before closing. Compare reviewed intermediaries now.</p>
        </div>
      </div>
      <Link to="/directory" className="btn-ghost" data-testid="home-browse-directory-button">Browse the directory <ArrowRight size={16} /></Link>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <InvestorStrip />
      <HowItWorks />
      <BrowseStates />
      <FoundingCTA />
    </div>
  );
}
